import { all, takeEvery, put, call, select } from "@redux-saga/core/effects";
import { AnyAction } from "redux";
import {
  EnumButtonModeType,
  EnumStatus,
  EnumPageMode,
} from "../../../common/enums/common.enums";
import {
  PaginationDTO,
  SearchParameterDTO,
  StandardSubjectDTO,
} from "../../../common/types/common.dto.types";
import { ActionResponseType } from "../../../common/types/common.types";
import { EnumPopupType } from "../../../components/Popups/Popup.enum";
import { openPopup } from "../../../components/Popups/Popup.reducer";
import { PopupProps } from "../../../components/Popups/PopupProps.types";
import { RootState } from "../../../core/store";
import { setLoading } from "../../layouts/main-layout/MainLayout.reducer";
import {
  DELETESTANDARDSUBJECT,
  GETSTANDRDSUBJECTLIST,
  SAVESTANDARDRESUBJECT,
  UPDATESTANDARDSUBJECT,
} from "./SubjectPage.action";
import {
  deleteStandardSubject,
  getStandardSubjectList,
  saveStandardSubject,
  updateStandardSubject,
} from "./SubjectPage.api";
import {
  setPageMode,
  setStandardSubject,
  setStandardSubjectList,
  setSubjectModalOpen,
} from "./SubjectPage.reducer";
import { SubjectPageType } from "./SubjectPage.types";

// ==============================|| VIEWS - PAGES - STANDARDSubject - SAGA ||============================== //

const getStandardSubjectListWatcher = function* (): any {
  yield takeEvery(GETSTANDRDSUBJECTLIST, function* (action: AnyAction): any {
    // set page loading on
    yield put(setLoading(true));

    try {
      // get data from backend
      const SubjectSearchParam: SearchParameterDTO = action.payload;
      const result: ActionResponseType = yield call(() =>
        getStandardSubjectList(SubjectSearchParam)
      );
      if (!result.IsSuccess) {
        yield put(
          openPopup({
            Open: true,
            BodyText: "An error occurred when getting data",
            HeaderText: "Warning",
            PopupType: EnumPopupType.WarningPopup,
          } as PopupProps)
        );
      } else {
        if (result === null) {
          yield put(
            openPopup({
              Open: true,
              BodyText: "No subject data to show",
              HeaderText: "Success",
              PopupType: EnumPopupType.SuccessPopup,
            } as PopupProps)
          );
        } else {
          const subjectpagetype: SubjectPageType = {
            Data: result.Data,
          } as SubjectPageType;

          yield put(
            setStandardSubjectList({
              ...subjectpagetype,
              Data: {
                Data: subjectpagetype.Data.Data,
                TotalRecordsCount: subjectpagetype.Data.TotalRecordsCount,
                SelectedPage: subjectpagetype.Data.SelectedPage,
              } as PaginationDTO,
            })
          );
        }
      }
    } catch (error) {
      yield put(
        openPopup({
          Open: true,
          BodyText: "An error occurred when getting data",
          HeaderText: "Warning",
          PopupType: EnumPopupType.WarningPopup,
        } as PopupProps)
      );
    }
    // set page loading on
    yield put(setLoading(false));
  });
};

const saveStandardSubjectWatcher = function* (): any {
  yield takeEvery(SAVESTANDARDRESUBJECT, function* (action: AnyAction): any {
    // set page loading
    yield put(setLoading(true));

    // save Subject data from backend
    const subjectPageType: SubjectPageType = action.payload;

    const result: ActionResponseType = yield call(() =>
      saveStandardSubject(subjectPageType.StandaradSubject)
    );

    if (!result.IsSuccess) {
      yield put(
        openPopup({
          Open: true,
          BodyText: result.ErrorMessage
            ? result.ErrorMessage
            : "An error occurred when saving data",
          HeaderText: "Warning",
          PopupType: EnumPopupType.WarningPopup,
        } as PopupProps)
      );
    } else {
      //set Page Mode with Data
      if (subjectPageType.ButtonModeType === EnumButtonModeType.Save) {
        yield put(setStandardSubject(result.Data));
        yield put(setPageMode(EnumPageMode.ViewMode));
      } else if (
        subjectPageType.ButtonModeType === EnumButtonModeType.SaveAndContinue
      ) {
        yield put(
          setStandardSubject({
            Id: 0,
            Name: "",
            StandardId: subjectPageType.StandaradSubject.StandardId,
            Version: 1,
            Status: EnumStatus.Published,
          } as StandardSubjectDTO)
        );
        yield put(setPageMode(EnumPageMode.AddMode));
      } else if (
        subjectPageType.ButtonModeType === EnumButtonModeType.SaveAndClose
      ) {
        yield put(
          setStandardSubject({
            Id: 0,
            Name: "",
            StandardId: subjectPageType.StandaradSubject.StandardId,
            Version: 1,
            Status: EnumStatus.Published,
          } as StandardSubjectDTO)
        );

        yield put(setPageMode(EnumPageMode.Default));
        yield put(setSubjectModalOpen(false));
      }
      //get Subject List
      const standardSubjectState: SubjectPageType = yield select(
        (s: RootState) => s.standardSubjectReducer
      );
      const listResult: ActionResponseType = yield call(() =>
        getStandardSubjectList({
          ...subjectPageType.StandardSubjectSearchParameter,
          SelectedPage: 1,
        })
      );
      if (!listResult.IsSuccess) {
        yield put(
          openPopup({
            Open: true,
            BodyText: "An error occurred when getting data",
            HeaderText: "Warning",
            PopupType: EnumPopupType.WarningPopup,
          } as PopupProps)
        );
      } else {
        if (result === null) {
          yield put(
            openPopup({
              Open: true,
              BodyText: "No subject data to show",
              HeaderText: "Success",
              PopupType: EnumPopupType.SuccessPopup,
            } as PopupProps)
          );
        } else {
          // if (
          //   subjectPageType.ButtonModeType ===
          //   EnumButtonModeType.SaveAndContinue
          // ) {
          yield put(
            openPopup({
              Open: true,
              BodyText: "Successfully saved",
              HeaderText: "Success",
              PopupType: EnumPopupType.SuccessPopup,
            } as PopupProps)
          );
          // }

          const subjectpagetype: SubjectPageType = {
            Data: listResult.Data,
          } as SubjectPageType;

          yield put(
            setStandardSubjectList({
              ...subjectpagetype,
              Data: {
                Data: subjectpagetype.Data.Data,
                TotalRecordsCount: subjectpagetype.Data.TotalRecordsCount,
                SelectedPage: 1,
              } as PaginationDTO,
            })
          );
        }
      }
    }

    // set page loading
    yield put(setLoading(false));
  });
};

const updateStandardSubjectWatcher = function* (): any {
  //
  yield takeEvery(UPDATESTANDARDSUBJECT, function* (action: AnyAction): any {
    // set page loading
    yield put(setLoading(true));

    // save reference data from backend

    const subjectPageType: SubjectPageType = action.payload;

    const result: ActionResponseType = yield call(() =>
      updateStandardSubject(subjectPageType.StandaradSubject)
    );
    if (!result.IsSuccess) {
      yield put(
        openPopup({
          Open: true,
          BodyText: result.ErrorMessage
            ? result.ErrorMessage
            : "An error occurred when updating data",
          HeaderText: "Warning",
          PopupType: EnumPopupType.WarningPopup,
        } as PopupProps)
      );
    } else {
      //set Page Mode with Data
      if (subjectPageType.ButtonModeType === EnumButtonModeType.Update) {
        yield put(setStandardSubject(result.Data));
        yield put(setPageMode(EnumPageMode.ViewMode));
      } else if (
        subjectPageType.ButtonModeType === EnumButtonModeType.UpdateAndClose
      ) {
        yield put(
          setStandardSubject({
            Id: 0,
            Name: "",
            StandardId: subjectPageType.StandaradSubject.StandardId,
            Version: 1,
            Status: EnumStatus.Published,
          } as StandardSubjectDTO)
        );
        yield put(setPageMode(EnumPageMode.Default));
        yield put(setSubjectModalOpen(false));
      }
      //get Subject List
      const standardSubjectState: SubjectPageType = yield select(
        (s: RootState) => s.standardSubjectReducer
      );
      const listResult: ActionResponseType = yield call(() =>
        getStandardSubjectList(subjectPageType.StandardSubjectSearchParameter)
      );
      if (!listResult.IsSuccess) {
        yield put(
          openPopup({
            Open: true,
            BodyText: "An error occurred when getting data",
            HeaderText: "Warning",
            PopupType: EnumPopupType.WarningPopup,
          } as PopupProps)
        );
      } else {
        if (listResult === null) {
          yield put(
            openPopup({
              Open: true,
              BodyText: "No subject data to show",
              HeaderText: "Success",
              PopupType: EnumPopupType.SuccessPopup,
            } as PopupProps)
          );
        } else {
          const subjectpagetype: SubjectPageType = {
            Data: listResult.Data,
          } as SubjectPageType;

          yield put(
            setStandardSubjectList({
              ...subjectpagetype,
              Data: {
                Data: subjectpagetype.Data.Data,
                TotalRecordsCount: subjectpagetype.Data.TotalRecordsCount,
                SelectedPage: subjectpagetype.Data.SelectedPage,
              } as PaginationDTO,
            })
          );
        }
      }
    }

    // set page loading
    yield put(setLoading(false));
  });
};

const deleteStandardSubjectWatcher = function* (): any {
  //
  yield takeEvery(DELETESTANDARDSUBJECT, function* (action: AnyAction): any {
    // set page loading
    yield put(setLoading(true));

    // save Subject data from backend
    const subjectPageType: SubjectPageType = action.payload;

    const result: ActionResponseType = yield call(() =>
      deleteStandardSubject(subjectPageType.StandaradSubject.Id)
    );
    if (!result.IsSuccess) {
      yield put(
        openPopup({
          Open: true,
          BodyText: result.ErrorMessage
            ? result.ErrorMessage
            : "An error occurred when deactivating data",
          HeaderText: "Warning",
          PopupType: EnumPopupType.WarningPopup,
        } as PopupProps)
      );
    } else {
      const standardSubjectState: SubjectPageType = yield select(
        (s: RootState) => s.standardSubjectReducer
      );

      //set Page Mode with Data
      yield put(
        setStandardSubject({
          Id: 0,
          Name: "",
          StandardId: standardSubjectState.StandaradSubject.StandardId,
          Version: 1,
          Status: EnumStatus.Published,
        } as StandardSubjectDTO)
      );
      yield put(setPageMode(EnumPageMode.Default));
      yield put(setSubjectModalOpen(false));

      //get Subject List
      const listResult: ActionResponseType = yield call(() =>
        getStandardSubjectList({
          ...subjectPageType.StandardSubjectSearchParameter,
          SelectedPage: 1,
          GridPageSize:
            standardSubjectState.StandardSubjectSearchParameter.GridPageSize,
        } as SearchParameterDTO)
      );
      if (!listResult.IsSuccess) {
        yield put(
          openPopup({
            Open: true,
            BodyText: "An error occurred when getting data",
            HeaderText: "Warning",
            PopupType: EnumPopupType.WarningPopup,
          } as PopupProps)
        );
      } else {
        if (listResult === null) {
          yield put(
            openPopup({
              Open: true,
              BodyText: "No reference data to show",
              HeaderText: "Success",
              PopupType: EnumPopupType.SuccessPopup,
            } as PopupProps)
          );
        } else {
          const subjectpagetype: SubjectPageType = {
            Data: listResult.Data,
          } as SubjectPageType;

          yield put(
            setStandardSubjectList({
              ...subjectpagetype,
              Data: {
                Data: subjectpagetype.Data.Data,
                TotalRecordsCount: subjectpagetype.Data.TotalRecordsCount,
                SelectedPage: subjectpagetype.Data.SelectedPage,
              } as PaginationDTO,
            })
          );
        }
      }
    }

    // set page loading
    yield put(setLoading(false));
  });
};

const standardSubjectSaga = function* () {
  yield all([
    getStandardSubjectListWatcher(),
    saveStandardSubjectWatcher(),
    updateStandardSubjectWatcher(),
    deleteStandardSubjectWatcher(),
  ]);
};

export default standardSubjectSaga;
